/**
 * Whether two records describe the same human, for the duplicate-party guards.
 *
 * Deliberately separate from `kybIsSamePerson`: that one reconciles rows already known to belong to
 * one person and short-circuits on individualId, so two distinct individual rows for the same human
 * read as different people. Here that is precisely the case to catch.
 *
 * `personIdentityMatch` applies the shared `compareIdentity` rule, so a middle name held on one side
 * only is ignored: a registry that holds one and a broker who does not are the same person.
 */

export interface PersonIdentity {
  givenName?: string | null;
  surname?: string | null;
  /** YYYY-MM-DD; longer ISO strings are accepted and truncated to the calendar day. */
  dateOfBirth?: string | null;
}

/** A person already on the application, carrying where they appear so the message can say so. */
export interface ExistingPersonRef extends ComparableIdentity {
  location: string;
}

/**
 * `exact` is a name and date of birth match and is refused outright. `name-only` is a name match
 * where at least one side has no date of birth - common for a registry record - and is a warning the
 * user can confirm past.
 */
export type IdentityMatch = 'none' | 'exact' | 'name-only';

export interface DuplicatePersonHit {
  ref: ExistingPersonRef;
  match: Exclude<IdentityMatch, 'none'>;
}

const calendarDay = (value?: string | null): string => (value ?? '').trim().slice(0, 10);

const normalizePartialMatchName = (value?: string | null): string => (value ?? '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase()
  .replace(/\s+/g, ' ');

const matchingNonEmptyValues = (a: string, b: string): boolean => !!a && !!b && a === b;

/**
 * Manual KYB reconciliation candidate rule: any two non-empty identity fields must match.
 * A different third field does not veto the pair, so equal first and last names still qualify when
 * both records hold different dates of birth. The reviewer must explicitly choose the final link.
 */
export function personIdentityPartialMatch(a: PersonIdentity, b: PersonIdentity): boolean {
  const matches = [
    matchingNonEmptyValues(
      normalizePartialMatchName(a.givenName),
      normalizePartialMatchName(b.givenName),
    ),
    matchingNonEmptyValues(
      normalizePartialMatchName(a.surname),
      normalizePartialMatchName(b.surname),
    ),
    matchingNonEmptyValues(calendarDay(a.dateOfBirth), calendarDay(b.dateOfBirth)),
  ];
  return matches.filter(Boolean).length >= 2;
}

/** `compareIdentity` collapsed to what a duplicate-person guard does with it: block, warn, or nothing. */
export function personIdentityMatch(a: ComparableIdentity, b: ComparableIdentity): IdentityMatch {
  switch (compareIdentity(a, b)) {
    case 'exact':
      return 'exact';
    case 'name-one-dob':
    case 'name-no-dob':
      return 'name-only';
    default:
      return 'none';
  }
}

/**
 * Only `exact` and `name-one-dob` are strong enough to link two records automatically, and only when
 * there is a single candidate. `name-one-dob` is the registry case: FrankieOne often holds no date of
 * birth for a person the broker entered with one. `partial` means only the first word of the first
 * name and the surname agree, which is left to a manual merge.
 */
export type IdentityComparison = 'exact' | 'name-one-dob' | 'name-no-dob' | 'dob-conflict' | 'partial' | 'none';

export interface ComparableIdentity extends PersonIdentity {
  middleName?: string | null;
}

// Apostrophes are dropped and every other separator becomes a space, so "Mary-Jane O'Brien" and
// "Mary Jane OBrien" agree. Letters of any script are kept: stripping to a-z would make every
// non-Latin name equal to every other.
const identityName = (value?: string | null): string => (value ?? '')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/['’‘`]/g, '')
  .replace(/[^\p{L}\p{N}]+/gu, ' ')
  .trim();

const firstWord = (name: string): string => name.split(' ')[0];

/**
 * The single name and date-of-birth rule for deciding whether two records are the same human.
 *
 * A middle name may sit in the first-name field on one side and the middle-name field on the other,
 * so first and middle names are also compared joined: "Mary Jane" equals "Mary" + "Jane". A middle
 * name held on one side only is ignored, as in `personIdentityMatch`. Two different middle names are
 * not: they often belong to different people, so the pair is only `partial`. An initial is not
 * treated as agreeing with a full middle name.
 */
export function compareIdentity(a: ComparableIdentity, b: ComparableIdentity): IdentityComparison {
  const aGiven = identityName(a.givenName);
  const bGiven = identityName(b.givenName);
  const aSurname = identityName(a.surname);
  const bSurname = identityName(b.surname);

  // A record with no name at all would otherwise match every other nameless one.
  if ((!aGiven && !aSurname) || (!bGiven && !bSurname) || aSurname !== bSurname) {
    return 'none';
  }

  const aMiddle = identityName(a.middleName);
  const bMiddle = identityName(b.middleName);
  const sameName = (aGiven === bGiven && (!aMiddle || !bMiddle || aMiddle === bMiddle))
    || identityName(`${a.givenName ?? ''} ${a.middleName ?? ''}`) === identityName(`${b.givenName ?? ''} ${b.middleName ?? ''}`);
  if (!sameName) {
    return aGiven && bGiven && firstWord(aGiven) === firstWord(bGiven) ? 'partial' : 'none';
  }

  const aDob = calendarDay(a.dateOfBirth);
  const bDob = calendarDay(b.dateOfBirth);
  if (aDob && bDob) {
    return aDob === bDob ? 'exact' : 'dob-conflict';
  }
  return aDob || bDob ? 'name-one-dob' : 'name-no-dob';
}

/**
 * The only rule for linking a person to an existing application individual without a human choosing
 * one. It never guesses: an exact name and date-of-birth match links when it is the only one, and a
 * name match with one date of birth missing links only when nobody else on the application shares the
 * name. A date-of-birth conflict never links. Anything else is left for staff to merge.
 *
 * `identityOf` lets callers pass their own row shapes; each caller still decides which rows are
 * candidates (all live individuals, or only the rows a save owns).
 */
export const pickIndividualMatch = <T>(
  identity: ComparableIdentity,
  candidates: readonly T[],
  identityOf: (candidate: T) => ComparableIdentity,
): T | null => {
  const results = candidates.map(candidate => ({ candidate, match: compareIdentity(identity, identityOf(candidate)) }));
  const exact = results.filter(result => result.match === 'exact');
  if (exact.length) {
    return exact.length === 1 ? exact[0].candidate : null;
  }
  // A namesake born on another day still shares the name: the record without a date of birth may be them.
  const sameName = results.filter(result =>
    result.match === 'name-one-dob' || result.match === 'name-no-dob' || result.match === 'dob-conflict');
  return sameName.length === 1 && sameName[0].match === 'name-one-dob' ? sameName[0].candidate : null;
};

export interface IdentityPairing {
  /** Row index each paired entry index resolved to. */
  rowFor: Map<number, number>;
}

/**
 * Pairs entries with rows of the same person, for a save re-matching records it wrote itself. An exact
 * name and date-of-birth match pairs in order: two rows that agree on both are one human stored twice.
 * So does a name match where neither side has a date of birth, which is how an entry rebuilt from a
 * stored row without one comes back. A name match with one date of birth missing pairs only as
 * `pickIndividualMatch` would link it: when it is the entry's only unpaired row sharing the name.
 * A date-of-birth conflict never pairs.
 */
export const pairIndividualsByIdentity = (
  entries: readonly ComparableIdentity[],
  rows: readonly ComparableIdentity[],
): IdentityPairing => {
  const comparisons = entries.map(entry => rows.map(row => compareIdentity(entry, row)));
  const rowFor = new Map<number, number>();
  const claimed = new Set<number>();
  const sharesName = (match: IdentityComparison) =>
    match === 'name-one-dob' || match === 'name-no-dob' || match === 'dob-conflict';
  const pairInOrder = (pairs: (match: IdentityComparison, byRow: IdentityComparison[]) => boolean) =>
    comparisons.forEach((byRow, entry) => {
      if (rowFor.has(entry)) return;
      const row = byRow.findIndex((match, index) => !claimed.has(index) && pairs(match, byRow));
      if (row >= 0) {
        rowFor.set(entry, row);
        claimed.add(row);
      }
    });
  pairInOrder(match => match === 'exact');
  pairInOrder((match, byRow) => match === 'name-no-dob'
    || (match === 'name-one-dob'
      && byRow.filter((other, index) => !claimed.has(index) && sharesName(other)).length === 1));
  return { rowFor };
};

/**
 * The strongest match against the people already on the application, or null. An exact hit anywhere
 * in the set wins over a name-only one, so the caller reports the blocking case when both exist.
 */
export function findDuplicatePerson(
  candidate: ComparableIdentity,
  existing: readonly ExistingPersonRef[],
): DuplicatePersonHit | null {
  let nameOnly: DuplicatePersonHit | null = null;
  for (const ref of existing) {
    const match = personIdentityMatch(candidate, ref);
    if (match === 'exact') {
      return { ref, match };
    }
    if (match === 'name-only' && !nameOnly) {
      nameOnly = { ref, match };
    }
  }
  return nameOnly;
}

/** "Dana Example is already on this application, as a key person of Acme Pty Ltd." */
export function duplicatePersonMessage(candidate: PersonIdentity, hit: DuplicatePersonHit): string {
  const name = `${(candidate.givenName ?? '').trim()} ${(candidate.surname ?? '').trim()}`.trim() || 'This person';
  if (hit.match === 'exact') {
    return `${name} is already on this application, as ${hit.ref.location}. Tick them as a guarantor on the existing record instead of adding them again.`;
  }
  const missingDob = calendarDay(hit.ref.dateOfBirth)
    ? 'No date of birth has been entered for this person'
    : 'That record has no date of birth';
  return `${name} may already be on this application, as ${hit.ref.location}. ${missingDob}, so this could be a different person - confirm to add them anyway.`;
}
